import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Aviso, Botao, EstadoVazio, formatarMoeda, Modal, Selo } from '../../componentes/ui';
import { useInserirLote } from '../../hooks/useCronogramaAcoes';
import { listarSpedsDisponiveis, type SpedDisponivel } from '../../servicos/operacao';
import { formatarCompetencia } from '../../servicos/formato';
import { ErroDaApi } from '../ErroDaApi';

/**
 * "Inserir e-mails" — o botão de lote do cronograma do Deca 1 (RN-082).
 *
 * ## A grade, e não uma lista
 *
 * O Deca 1 mostra uma lista de meses e pede o tipo de e-mail num campo à parte,
 * um envio por vez. Mas a operação real é "os quatro e-mails de março a agosto",
 * e digitar isso mês a mês é 24 idas ao servidor.
 *
 * Aqui é uma grade **mês × tipo**: cada célula é um agendamento. O que já está
 * na fila aparece desmarcado e travado, com o motivo — não some da tela, porque
 * "não consigo marcar março" sem explicação é pior que uma célula cinza.
 *
 * ## A data
 *
 * O Deca 1 agenda tudo para a mesma data. Mantido: quem insere em lote está
 * programando uma remessa, não datas individuais. Depois de criado, cada item
 * pode ser reprogramado pelo botão da linha.
 */
export function ModalDeInsercaoEmLote(props: {
  readonly clienteId: string;
  readonly ano: number;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const disponiveis = useQuery({
    queryKey: ['speds-disponiveis', props.clienteId, props.ano],
    queryFn: () => listarSpedsDisponiveis(props.clienteId, props.ano),
    enabled: props.aberto,
  });

  const inserir = useInserirLote(props.clienteId);

  // Chave da seleção: `calculoId:emailId` — a mesma que o servidor usa para
  // decidir duplicata.
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [data, setData] = useState(dataPadrao(props.ano));

  const linhas = disponiveis.data ?? [];
  const inseriveis = useMemo(
    () => new Set(linhas.flatMap((l) => l.tiposFaltantes.map((t) => `${l.calculoId}:${t}`))),
    [linhas],
  );

  const alternar = (chave: string): void => {
    setMarcados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  };

  /** Marca ou desmarca a coluna inteira de um tipo — o caso comum. */
  const alternarColuna = (tipo: string): void => {
    const daColuna = linhas
      .filter((l) => l.tiposFaltantes.includes(tipo))
      .map((l) => `${l.calculoId}:${tipo}`);
    const todosMarcados = daColuna.length > 0 && daColuna.every((c) => marcados.has(c));
    setMarcados((atual) => {
      const proximo = new Set(atual);
      for (const c of daColuna) {
        if (todosMarcados) proximo.delete(c);
        else proximo.add(c);
      }
      return proximo;
    });
  };

  const fechar = (): void => {
    inserir.reset();
    setMarcados(new Set());
    props.aoFechar();
  };

  const enviar = (): void => {
    const itens = [...marcados].map((chave) => {
      const [calculoId, emailId] = chave.split(':');
      return {
        calculoId: calculoId as string,
        emailId: emailId as string,
        dataAgendamento: `${data}T00:00:00.000Z`,
      };
    });
    inserir.mutate({ ano: props.ano, itens }, { onSuccess: () => setMarcados(new Set()) });
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Inserir e-mails no cronograma"
      descricao={`Competências de ${props.ano - 1} — operação de ${props.ano}`}
      largura="larga"
      rodape={
        inserir.isSuccess ? (
          <Botao onClick={fechar}>Concluir</Botao>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              onClick={enviar}
              disabled={marcados.size === 0 || data === ''}
              carregando={inserir.isPending}
            >
              {marcados.size === 0 ? 'Inserir' : `Inserir ${marcados.size} e-mail(s)`}
            </Botao>
          </>
        )
      }
    >
      {inserir.isSuccess ? (
        <Aviso
          tom={inserir.data.criados > 0 ? 'positivo' : 'atencao'}
          titulo={`${inserir.data.criados} agendamento(s) criado(s)`}
        >
          {inserir.data.ignorados > 0 ? (
            <>
              {inserir.data.ignorados} foram ignorados por já existirem na fila. O lote não duplica:
              se alguém agendou o mesmo e-mail entre a sua seleção e o clique, ele fica como está.
            </>
          ) : (
            <>Todos entraram como Agendado para {formatarData(data)}.</>
          )}
        </Aviso>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-conteudo-primario">Data de envio</span>
              <input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className="h-11 rounded-card border border-borda bg-superficie-base px-3 text-sm text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
              />
            </label>
            <p className="max-w-md pb-1 text-xs text-conteudo-sutil">
              Vale para todos os itens do lote. Cada um pode ser reprogramado depois, pela linha do
              cronograma.
            </p>
          </div>

          {disponiveis.isError && (
            <ErroDaApi titulo="Não foi possível carregar os cálculos" erro={disponiveis.error} />
          )}

          {disponiveis.isPending ? (
            <p className="py-8 text-center text-sm text-conteudo-sutil">Carregando cálculos…</p>
          ) : linhas.length === 0 ? (
            <EstadoVazio
              titulo={`Nenhum cálculo em ${props.ano - 1}`}
              descricao={`Não há SPED processado para as competências de ${props.ano - 1}. Envie os arquivos na aba de ICMS recorrente antes de montar a fila.`}
            />
          ) : (
            <Grade
              linhas={linhas}
              marcados={marcados}
              inseriveis={inseriveis}
              aoAlternar={alternar}
              aoAlternarColuna={alternarColuna}
            />
          )}

          {inserir.isError && <ErroDaApi titulo="Nada foi inserido" erro={inserir.error} />}
        </div>
      )}
    </Modal>
  );
}

/* ─── A grade ─────────────────────────────────────────────────────────────── */

function Grade(props: {
  readonly linhas: readonly SpedDisponivel[];
  readonly marcados: ReadonlySet<string>;
  readonly inseriveis: ReadonlySet<string>;
  readonly aoAlternar: (chave: string) => void;
  readonly aoAlternarColuna: (tipo: string) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-card border border-borda">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">
          Cálculos por competência e tipos de e-mail disponíveis para agendar
        </caption>
        <thead>
          <tr className="border-b border-borda bg-superficie-tabela-topo">
            <th scope="col" className="px-4 py-3 text-left font-medium text-conteudo-tabela-topo">
              Competência
            </th>
            <th scope="col" className="px-4 py-3 text-right font-medium text-conteudo-tabela-topo">
              ICMS apurado
            </th>
            {TIPOS.map((t) => (
              <th key={t.id} scope="col" className="px-3 py-3 text-center">
                <button
                  type="button"
                  onClick={() => props.aoAlternarColuna(t.id)}
                  className="mx-auto flex flex-col items-center gap-0.5 rounded px-2 py-1 text-xs font-medium text-conteudo-tabela-topo hover:bg-superficie-elevada focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
                  title={`Marcar ou desmarcar a coluna "${t.rotulo}"`}
                >
                  <span>{t.rotulo}</span>
                  <span className="text-[0.65rem] font-normal text-conteudo-sutil">
                    {t.destinatario}
                  </span>
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.linhas.map((l) => (
            <tr key={l.calculoId} className="border-b border-borda/60 last:border-0">
              <th
                scope="row"
                className="whitespace-nowrap px-4 py-3 text-left font-normal text-conteudo-primario"
              >
                {formatarCompetencia(l.competencia)}
              </th>
              <td className="valor-fiscal whitespace-nowrap px-4 py-3 text-right text-conteudo-secundario">
                {formatarMoeda(l.totalSped)}
              </td>
              {TIPOS.map((t) => {
                const chave = `${l.calculoId}:${t.id}`;
                const disponivel = props.inseriveis.has(chave);
                return (
                  <td key={t.id} className="px-3 py-3 text-center">
                    {disponivel ? (
                      <input
                        type="checkbox"
                        checked={props.marcados.has(chave)}
                        onChange={() => props.aoAlternar(chave)}
                        aria-label={`${t.rotulo} para ${formatarCompetencia(l.competencia)}`}
                        className="h-4 w-4 rounded border-borda bg-superficie-base text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
                      />
                    ) : (
                      <Selo tom="neutro" tamanho="pequeno">
                        Na fila
                      </Selo>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

/**
 * Os quatro tipos que se inserem por competência.
 *
 * A lista espelha `TIPOS_INSERIVEIS` do servidor — que é quem valida. Se
 * divergirem, o servidor recusa: a tela não tem a última palavra sobre o que a
 * fila aceita.
 */
const TIPOS = [
  {
    id: 'FORNECEDOR_TRANSFERENCIA_CREDITO',
    rotulo: 'Transferência',
    destinatario: 'fornecedor',
  },
  { id: 'CLIENTE_AJUSTE_CREDITO', rotulo: 'Ajuste', destinatario: 'cliente' },
  { id: 'CLIENTE_PAGAMENTO_CREDITO', rotulo: 'Pagamento', destinatario: 'cliente' },
  { id: 'ASSESSOR_HONORARIOS_CREDITO', rotulo: 'Honorários', destinatario: 'assessor' },
] as const;

/** Primeiro dia do mês corrente, se cair dentro do ano da operação. */
function dataPadrao(ano: number): string {
  const hoje = new Date();
  const alvo = hoje.getUTCFullYear() === ano ? hoje : new Date(Date.UTC(ano, 0, 1));
  return `${alvo.getUTCFullYear()}-${String(alvo.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

function formatarData(iso: string): string {
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}
